<script setup>
import { computed, inject, onMounted, ref } from "vue";
import { AI, Dashboard, Review } from "@/services";
import { useMainStore } from "@/stores/main.js";
import { mapPeriodToDateRange } from "@/utils";
import MHeader from "@/components/m/MHeader.vue";
import MPullScroll from "@/components/m/MPullScroll.vue";
import MSheet from "@/components/m/MSheet.vue";
import MToast from "@/components/m/MToast.vue";
import MIcon from "@/components/m/MIcon.vue";
import MStars from "@/components/m/MStars.vue";
import MSwipeCard from "@/components/m/MSwipeCard.vue";
import BtSourceMark from "@/components/bt/BtSourceMark.vue";

const ctx = inject("mctx");
const mainStore = useMainStore();
const intent = ctx.intent.value || {};

const SOURCE_LABELS = {
  GOOGLE: "Google",
  HELLOPETER: "Hellopeter",
  FACEBOOK: "Facebook",
  TRIPADVISOR: "TripAdvisor",
  FRESHA: "Fresha",
};
const sourceLabel = (v) =>
  SOURCE_LABELS[v] || (v ? v[0] + v.slice(1).toLowerCase() : "Unknown");
// Preferred display order; sources connected but unlisted are appended after.
const SOURCE_ORDER = [
  "GOOGLE",
  "HELLOPETER",
  "FACEBOOK",
  "TRIPADVISOR",
  "FRESHA",
];
const SORT_OPTIONS = [
  ["Newest", "newest"],
  ["Oldest", "oldest"],
  ["Rating: high to low", "rating_desc"],
  ["Rating: low to high", "rating_asc"],
];
const SORT_SHORT = {
  newest: "Newest",
  oldest: "Oldest",
  rating_desc: "Rating ↓",
  rating_asc: "Rating ↑",
};
const TONES = ["Apologetic", "Professional", "Warm"];
const TAB_KEYS = ["needs", "all", "negative", "positive", "replied"];

/* ---- state ---- */
const tab = ref(
  intent.tab != null ? intent.tab : intent.topic || intent.source ? 1 : 0,
);
const source = ref(intent.source || null);
const topic = ref(intent.topic || mainStore.selectedTopic || null);
const sort = ref("newest");
const query = ref("");
const sortOpen = ref(false);
const items = ref([]);
const counts = ref({
  needs: null,
  all: null,
  negative: null,
  positive: null,
  replied: null,
});
const loading = ref(false);
const loadingMore = ref(false);
const page = ref(1);
const hasMore = ref(false);
const PAGE_SIZE = 20;
let searchTimer = null;

const sheetId = ref(null);
const tone = ref(0);
const editing = ref(false);
const toast = ref("");
const drafts = ref({}); // { [toneIndex]: replyText } cache for the open review
const generating = ref(false);
const genError = ref("");

const editableReply = computed({
  get: () => drafts.value[tone.value] || "",
  set: (val) => {
    drafts.value = { ...drafts.value, [tone.value]: val };
  },
});

/* ---- helpers ---- */
function age(value) {
  if (!value) return "";
  const diff = (Date.now() - new Date(value).getTime()) / 1000;
  if (Number.isNaN(diff)) return "";
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))}m`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h`;
  return `${Math.round(diff / 86400)}d`;
}
function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}
const sentClass = (v) =>
  Number(v) < 0 ? "neg" : Number(v) > 0 ? "pos" : "neu";
const sentLabel = (v) =>
  Number(v) < 0 ? "Negative" : Number(v) > 0 ? "Positive" : "Neutral";

function isReplied(r) {
  return Boolean(r.response_content);
}

/* ---- normalized rows for the card / sheet ---- */
const rows = computed(() =>
  items.value.map((r) => ({
    raw: r,
    id: r.id,
    source: r.source,
    name: sourceLabel(r.source),
    rating: Number(r.rating) || 0,
    age: age(r.created),
    date: formatDate(r.created),
    title: r.title || "",
    body: r.content || "",
    emotion: r.emotion || "",
    topic: r.topic || "",
    sentiment: r.sentiment,
    sent: sentClass(r.sentiment),
    // Urgent requires negativity AND either a written complaint or a real star
    // rating (>=1); an unrated, comment-less review can never be Urgent.
    urgent:
      !isReplied(r) &&
      Number(r.sentiment) < 0 &&
      (Boolean(String(r.content ?? "").trim()) || Number(r.rating) >= 1),
    replied: isReplied(r),
  })),
);

const TAB_DEFS = computed(() => [
  ["Needs reply", counts.value.needs],
  ["All", counts.value.all],
  ["Negative", counts.value.negative],
  ["Positive", counts.value.positive],
  ["Replied", counts.value.replied],
]);

// Source filter chips, derived from the company's actually-connected channels
// (scoped to the selected location) instead of a hardcoded list — matches
// desktop and only shows when there's more than one source to pick.
const availableSources = computed(() => {
  const channels = mainStore.channels || [];
  const scopeId = locationId();
  const set = new Set(
    channels
      .filter((ch) => !scopeId || String(ch.location_id) === String(scopeId))
      .map((ch) => ch.source)
      .filter(Boolean),
  );
  return [...set].sort((a, b) => {
    const ai = SOURCE_ORDER.indexOf(a);
    const bi = SOURCE_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
  });
});
const showSourceFilter = computed(() => availableSources.value.length > 1);
const sortShort = computed(() => SORT_SHORT[sort.value] || "Sort");

// Read a list-or-single field off a raw review (topics/root_causes), mirroring
// the desktop helper so the detail sheet can show the same metadata.
const labelList = (r, listKey, fallbackKey) =>
  r?.[listKey]?.length ? r[listKey] : r?.[fallbackKey] ? [r[fallbackKey]] : [];

const headerSub = computed(() => {
  if (topic.value || source.value) {
    return (
      "Filtered" +
      (topic.value ? " · " + topic.value : "") +
      (source.value ? " · " + sourceLabel(source.value) : "")
    );
  }
  const total = counts.value.all;
  return total != null
    ? `${total.toLocaleString()} reviews`
    : "Customer feedback";
});

const sel = computed(
  () => rows.value.find((r) => r.id === sheetId.value) || null,
);

// Mirrors the desktop split: sentiment + emotion are one always-present verdict,
// while topics/root causes/entities are extractions shown only when non-empty.
const topicList = computed(() => labelList(sel.value?.raw, "topics", "topic"));
const rootCauseList = computed(() =>
  labelList(sel.value?.raw, "root_causes", "root_cause"),
);
const entityList = computed(() => sel.value?.raw?.entities || []);
const hasAiEvidence = computed(() =>
  Boolean(
    topicList.value.length ||
    rootCauseList.value.length ||
    entityList.value.length,
  ),
);
const currentDraft = computed(() => drafts.value[tone.value] || "");
const hasDraft = computed(() => Boolean(drafts.value[tone.value]));

/* ---- data ---- */
function dateBounds() {
  const { startDate, endDate } = mapPeriodToDateRange(
    mainStore.selectedPeriod,
    mainStore.customRange,
  );
  return [startDate.toISOString(), endDate.toISOString()];
}
function locationId() {
  const location = mainStore.selectedLocation;
  const id = location?.location_id || location?.id;
  return id === "ALL" ? null : id;
}
function sentimentForTab(key) {
  if (key === "negative" || key === "needs") return "-1";
  if (key === "positive") return "1";
  return null;
}

async function fetchReviews({ append = false } = {}) {
  // activeCompanyId honours the read-only competitor view.
  const companyId = mainStore.activeCompanyId;
  if (!companyId) {
    items.value = [];
    hasMore.value = false;
    return;
  }
  const key = TAB_KEYS[tab.value];
  const [start, end] = dateBounds();
  if (append) {
    loadingMore.value = true;
  } else {
    loading.value = true;
    page.value = 1;
  }
  try {
    if (key === "needs") {
      // The priority endpoint is limit-based, so "load more" grows the limit
      // and replaces the list (it always returns from the top).
      const data = await Dashboard.getPriorityReviews(
        companyId,
        start,
        end,
        page.value * PAGE_SIZE,
        locationId(),
        query.value || null,
        source.value || null,
        topic.value || null,
      );
      const list = (data.items || []).map((r) => ({
        ...r,
        created: r.review_created,
        response_content: null,
      }));
      items.value = list;
      hasMore.value = list.length < (data.count ?? list.length);
    } else {
      const response = await Review.getReviewsByCompany(
        companyId,
        locationId(),
        start,
        end,
        page.value,
        PAGE_SIZE,
        sentimentForTab(key),
        source.value || null,
        topic.value || null,
        null,
        key === "replied" ? true : null,
        sort.value,
        query.value || null,
      );
      const batch = response.items || [];
      items.value = append ? [...items.value, ...batch] : batch;
      const total = response.total ?? items.value.length;
      hasMore.value = items.value.length < total;
    }
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
}

function loadMore() {
  if (loading.value || loadingMore.value || !hasMore.value) return;
  page.value += 1;
  fetchReviews({ append: true });
}

function setSort(value) {
  sort.value = value;
  sortOpen.value = false;
  fetchReviews();
}

function onSearchInput() {
  clearTimeout(searchTimer);
  // Counts are now search-aware, so the badges have to move with the list.
  searchTimer = setTimeout(() => {
    fetchReviews();
    fetchCounts();
  }, 300);
}

async function fetchCounts() {
  const companyId = mainStore.activeCompanyId;
  if (!companyId) return;
  const [start, end] = dateBounds();
  counts.value = await Review.getReviewCounts(
    companyId,
    locationId(),
    start,
    end,
    source.value || null,
    topic.value || null,
    query.value || null,
  );
}

function setTab(i) {
  tab.value = i;
  fetchReviews();
}
function toggleSource(key) {
  source.value = source.value === key ? null : key;
  fetchReviews();
  fetchCounts();
}
function clearFilters() {
  source.value = null;
  topic.value = null;
  mainStore.setSelectedTopic(null);
  fetchReviews();
  fetchCounts();
}

function openSheet(id) {
  sheetId.value = id;
  tone.value = 0;
  editing.value = false;
  drafts.value = {};
  generating.value = false;
  genError.value = "";
}
async function generateReply() {
  if (!sel.value || generating.value) return;
  generating.value = true;
  genError.value = "";
  try {
    const companyId = mainStore.selectedCompany?.company_id;
    const data = await AI.generateDraftReply(
      companyId,
      sel.value.raw.id,
      TONES[tone.value],
    );
    if (data && data.reply) {
      drafts.value = { ...drafts.value, [tone.value]: data.reply };
    }
    // data === null => demo-blocked; the nudge modal is already shown.
  } catch (e) {
    console.error("draft reply failed", e);
    genError.value = "Could not generate a reply. Please try again.";
  } finally {
    generating.value = false;
  }
}
async function copyReply() {
  if (!currentDraft.value) return;
  try {
    await navigator.clipboard.writeText(currentDraft.value);
    showToast("Copied to clipboard");
  } catch (e) {
    console.error("clipboard write failed", e);
    showToast("Couldn't copy — please copy manually");
  }
}
function showToast(msg) {
  toast.value = msg;
  setTimeout(() => (toast.value = ""), 2200);
}
function onRefresh() {
  fetchReviews();
  fetchCounts();
  showToast("Feed updated");
}

onMounted(() => {
  fetchReviews();
  fetchCounts();
});
</script>

<template>
  <div class="btm-app">
    <MHeader
      :title="tab === 0 ? 'Needs response' : 'Reviews'"
      :sub="headerSub"
    />

    <!-- filter tabs -->
    <div class="btm-rail" style="padding-bottom: 8px">
      <button
        v-for="(def, i) in TAB_DEFS"
        :key="def[0]"
        class="btm-chip"
        :class="i === tab ? (i === 0 ? 'on-neg' : 'on') : ''"
        @click="setTab(i)"
      >
        <MIcon v-if="i === 0" name="flame" :size="12" fill />{{ def[0]
        }}<span v-if="def[1] != null" class="btm-mono" style="opacity: 0.6">{{
          def[1].toLocaleString()
        }}</span>
      </button>
    </div>

    <!-- search + sort row -->
    <div
      style="
        display: flex;
        gap: 8px;
        padding: 2px 16px 10px;
        align-items: center;
      "
    >
      <label
        style="
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--surface-2);
          border: 1px solid var(--border);
          border-radius: var(--pill);
          padding: 0 12px;
          height: 38px;
        "
      >
        <MIcon name="search" :size="15" />
        <input
          v-model="query"
          type="search"
          inputmode="search"
          placeholder="Search reviews…"
          aria-label="Search reviews"
          style="
            flex: 1;
            min-width: 0;
            border: 0;
            background: transparent;
            outline: none;
            font-family: inherit;
            font-size: 13.5px;
            color: var(--text);
          "
          @input="onSearchInput"
        />
        <button
          v-if="query"
          type="button"
          aria-label="Clear search"
          style="
            border: 0;
            background: transparent;
            color: var(--text-3);
            cursor: pointer;
            padding: 0;
            line-height: 1;
          "
          @click="
            query = '';
            onSearchInput();
          "
        >
          ✕
        </button>
      </label>
      <button
        type="button"
        class="btm-chip"
        style="height: 38px; flex: 0 0 auto"
        aria-label="Sort reviews"
        @click="sortOpen = true"
      >
        <MIcon name="filter" :size="14" />{{ sortShort }}
      </button>
    </div>

    <!-- source filter row (only when more than one source is connected) -->
    <div
      v-if="showSourceFilter"
      style="display: flex; align-items: center; gap: 9px; padding: 0 16px 10px"
    >
      <span class="btm-muted" style="font-size: 11px; font-weight: 700"
        >Source</span
      >
      <button
        v-for="key in availableSources"
        :key="key"
        :title="sourceLabel(key)"
        :aria-label="`Filter by ${sourceLabel(key)}`"
        :aria-pressed="source === key"
        :style="{
          border: 'none',
          background: 'transparent',
          padding: 0,
          borderRadius: '7px',
          cursor: 'pointer',
          opacity: !source || source === key ? 1 : 0.32,
          boxShadow: source === key ? '0 0 0 2px var(--brand)' : 'none',
        }"
        @click="toggleSource(key)"
      >
        <BtSourceMark :source="key" :size="26" />
      </button>
      <button
        v-if="topic || source"
        class="btm-chip"
        style="margin-left: auto; height: 26px"
        @click="clearFilters"
      >
        Clear
      </button>
    </div>

    <MPullScroll :busy="loading" @refresh="onRefresh" @load-more="loadMore">
      <div class="btm-pad" style="padding-top: 4px; padding-bottom: 16px">
        <div
          v-if="rows.length"
          style="
            display: flex;
            align-items: center;
            gap: 6px;
            color: var(--text-3);
            font-size: 11px;
            margin: 0 0 10px;
            justify-content: center;
          "
        >
          <MIcon
            name="chevright"
            :size="12"
            style="transform: rotate(180deg)"
          />Swipe a card left to reply
        </div>
        <MSwipeCard
          v-for="r in rows"
          :key="r.id"
          :r="r"
          :replied="r.replied"
          @reply="openSheet(r.id)"
          @open="openSheet(r.id)"
        />
        <div
          v-if="!rows.length && !loading"
          style="
            padding: 30px;
            text-align: center;
            color: var(--text-3);
            font-size: 13px;
          "
        >
          Nothing here 🎉
        </div>
        <div
          v-if="loading"
          style="
            padding: 30px;
            text-align: center;
            color: var(--text-3);
            font-size: 13px;
          "
        >
          Loading…
        </div>
        <!-- infinite scroll auto-loads; this button is the reliable fallback -->
        <button
          v-if="hasMore && !loading"
          type="button"
          class="btm-btn btm-btn-ghost"
          style="margin-top: 4px"
          :disabled="loadingMore"
          @click="loadMore"
        >
          {{ loadingMore ? "Loading…" : "Load more" }}
        </button>
      </div>
    </MPullScroll>

    <MToast :msg="toast" :show="!!toast" />

    <!-- sort picker sheet -->
    <MSheet :open="sortOpen" label="Sort by" @close="sortOpen = false">
      <div style="font-weight: 800; font-size: 16px; margin-bottom: 6px">
        Sort by
      </div>
      <button
        v-for="[label, value] in SORT_OPTIONS"
        :key="value"
        type="button"
        class="btm-trow"
        style="
          width: 100%;
          justify-content: space-between;
          background: none;
          border: 0;
          border-top: 1px solid var(--border);
          font-family: inherit;
          font-size: 14px;
          color: var(--text);
          cursor: pointer;
        "
        @click="setSort(value)"
      >
        <span>{{ label }}</span>
        <MIcon v-if="sort === value" name="check" :size="18" />
      </button>
    </MSheet>

    <!-- AI reply sheet -->
    <MSheet
      :open="sheetId != null"
      label="Review detail"
      @close="sheetId = null"
    >
      <div v-if="sel">
        <div
          style="
            display: flex;
            gap: 12px;
            align-items: center;
            margin-bottom: 14px;
          "
        >
          <BtSourceMark :source="sel.source" :size="40" />
          <div style="flex: 1; min-width: 0">
            <div style="display: flex; align-items: center; gap: 8px">
              <span style="font-weight: 800; font-size: 16px">{{
                sel.name
              }}</span>
              <MStars :value="sel.rating" :size="13" />
            </div>
            <div class="btm-muted" style="margin-top: 2px">
              {{ sel.name
              }}<template v-if="sel.date"> · {{ sel.date }}</template>
            </div>
          </div>
        </div>
        <div
          v-if="sel.title"
          style="font-weight: 800; font-size: 15px; margin-bottom: 6px"
        >
          {{ sel.title }}
        </div>
        <p
          style="
            margin: 0 0 14px;
            font-size: 13.5px;
            line-height: 1.55;
            color: var(--text-2);
          "
          :style="
            sel.body ? {} : { fontStyle: 'italic', color: 'var(--text-3)' }
          "
        >
          {{ sel.body || "Rating-only review — no written comment." }}
        </p>
        <div class="btm-ai">
          <span class="btm-verdict" :class="sentClass(sel.sentiment)">
            <span class="dot" />
            {{ sentLabel(sel.sentiment) }}
            <template v-if="sel.emotion">
              <span class="sep">·</span>
              <span class="emo">{{ sel.emotion }}</span>
            </template>
          </span>

          <div v-if="hasAiEvidence" class="btm-evidence">
            <div v-if="topicList.length" class="btm-evgroup">
              <div class="btm-eyebrow">Topics</div>
              <div class="btm-evrow">
                <span
                  v-for="t in topicList"
                  :key="'t-' + t"
                  class="btm-ev strong"
                  >{{ t }}</span
                >
              </div>
            </div>

            <div v-if="rootCauseList.length" class="btm-evgroup">
              <div class="btm-eyebrow">Root causes</div>
              <div class="btm-evrow">
                <span
                  v-for="rc in rootCauseList"
                  :key="'rc-' + rc"
                  class="btm-ev"
                  >{{ rc }}</span
                >
              </div>
            </div>

            <!-- Entities carry their own sentiment, so a praised staff member
                 reads positive even inside a negative review. There's no
                 entities screen on mobile, so these stay plain (non-tappable)
                 tags rather than the buttons desktop uses. -->
            <div v-if="entityList.length" class="btm-evgroup">
              <div class="btm-eyebrow">Entities</div>
              <div class="btm-evrow">
                <span
                  v-for="e in entityList"
                  :key="e.label + ':' + e.type"
                  class="btm-ent"
                  :class="sentClass(e.sentiment)"
                  ><span class="d" />{{ e.label }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- open the original review at its source so it can be actioned -->
        <a
          v-if="sel.raw.review_link"
          :href="sel.raw.review_link"
          target="_blank"
          rel="noopener"
          class="btm-btn btm-btn-ghost"
          style="text-decoration: none; margin-bottom: 16px"
        >
          <MIcon name="external" :size="16" />Open in {{ sel.name }}
        </a>

        <div
          v-if="!mainStore.isReadOnly"
          class="btm-card"
          style="overflow: hidden; border-color: var(--brand-line)"
        >
          <div
            style="
              display: flex;
              align-items: center;
              gap: 9px;
              padding: 12px 14px;
              background: var(--brand-soft);
            "
          >
            <span
              style="
                width: 26px;
                height: 26px;
                border-radius: 7px;
                display: grid;
                place-items: center;
                background: var(--brand);
                color: #2a1c00;
              "
              ><MIcon name="sparkles" :size="14" fill
            /></span>
            <div
              style="font-weight: 800; font-size: 13.5px; white-space: nowrap"
            >
              AI Suggested Reply
            </div>
          </div>
          <div style="padding: 14px">
            <div class="btm-seg" style="margin-bottom: 12px">
              <button
                v-for="(t, i) in TONES"
                :key="t"
                :class="{ on: i === tone }"
                @click="
                  tone = i;
                  genError = '';
                "
              >
                {{ t }}
              </button>
            </div>
            <div
              v-if="genError"
              style="
                padding: 12px;
                border-radius: 12px;
                background: var(--neg-soft);
                color: var(--neg);
                font-size: 13px;
                margin-bottom: 10px;
              "
            >
              {{ genError }}
            </div>
            <textarea
              v-if="hasDraft && editing"
              v-model="editableReply"
              rows="5"
              style="
                padding: 13px;
                border-radius: 12px;
                background: var(--surface-2);
                border: 1px solid var(--border);
                font-size: 13.5px;
                line-height: 1.55;
                width: 100%;
                resize: vertical;
                box-sizing: border-box;
                font-family: inherit;
                color: var(--text);
              "
            />
            <div
              v-else-if="hasDraft"
              style="
                padding: 13px;
                border-radius: 12px;
                background: var(--surface-2);
                border: 1px solid var(--border);
                font-size: 13.5px;
                line-height: 1.55;
              "
            >
              {{ currentDraft }}
            </div>
            <div
              v-else
              style="
                padding: 13px;
                border-radius: 12px;
                background: var(--surface-2);
                border: 1px solid var(--border);
                font-size: 13px;
                line-height: 1.5;
                color: var(--text-soft);
              "
            >
              Generate an AI reply drafted from this review in a{{
                /^[aeiou]/i.test(TONES[tone]) ? "n" : ""
              }}
              {{ TONES[tone] }} tone.
            </div>
          </div>
        </div>

        <div
          style="display: flex; flex-wrap: wrap; gap: 10px; margin-top: 16px"
        >
          <button
            class="btm-btn btm-btn-ghost"
            style="flex: 0 0 auto; width: auto; padding: 0 16px"
            @click="sheetId = null"
          >
            Close
          </button>
          <button
            v-if="!hasDraft && !mainStore.isReadOnly"
            class="btm-btn btm-btn-primary"
            :disabled="generating"
            @click="generateReply"
          >
            <MIcon
              :name="generating ? 'loader' : 'sparkles'"
              :size="16"
              :class="{ 'btm-spin': generating }"
            />{{ generating ? "Generating…" : "Generate AI Reply" }}
          </button>
          <template v-else-if="!mainStore.isReadOnly">
            <button
              class="btm-btn btm-btn-ghost"
              style="flex: 0 0 auto; width: auto; padding: 0 16px"
              :disabled="generating"
              @click="generateReply"
            >
              <MIcon
                :name="generating ? 'loader' : 'sparkles'"
                :size="16"
                :class="{ 'btm-spin': generating }"
              />{{ generating ? "Regenerating…" : "Regenerate" }}
            </button>
            <button
              class="btm-btn btm-btn-ghost"
              style="flex: 0 0 auto; width: auto; padding: 0 16px"
              @click="editing = !editing"
            >
              {{ editing ? "Done" : "Edit" }}
            </button>
            <button class="btm-btn btm-btn-primary" @click="copyReply">
              <MIcon name="copy" :size="16" />Copy reply
            </button>
          </template>
        </div>
      </div>
    </MSheet>
  </div>
</template>
